import React, { useState, useEffect } from "react";
import { ProcedureEntity } from "../../../../application/domain/procedures/procedure-entity";
import {
  ContentsHeader,
  KebabMenu,
  LinkCopyButton,
} from "@graffer-inc/console-design-kit";
import { GrafferManagedTag } from "@graffer-inc/smart-apply";
import { ProcedureStatusTag } from "../../../molecules/procedure-status-tag";
import { useButtons } from "./use-buttons";
import { useMenuButton } from "./use-menu-button";
import { ResourceLoadingState } from "../../../../application/service/smart-apply/procedure-detail/resource-loading-state";
import apiClient from "../../../../application/apis/api-client";
import { toast } from "react-toastify";
import { formatDatetime } from "../../../../utils/date-utils";

export const ProcedureDetailHeader: React.FC<{
  procedure?: ProcedureEntity;
  orgSlug: string;
  currentCustomPath?: string;
  resourceLoadState: ResourceLoadingState;
  id: string;
  isAdmin: boolean;
  onManageGroupOpen: () => void;
  onPublishSettingModalOpen: () => void;
  onUnPublish: () => void;
  onPauseAccepting: () => void;
  onReturnToDraft: () => void;
  onResumeAccepting: () => void;
  onCopyOpen: () => void;
  onDeleteOpen: () => void;
  onCustomPathOpen: () => void;
  onExportProcedure: () => void;
  onMatrixCodeOpen: () => void;
  canShowPreview: boolean;
  onOpenPreview: () => void;
  onArchiveOpen: () => void;
  onUnarchive: () => void;
}> = ({
  procedure,
  orgSlug,
  currentCustomPath,
  resourceLoadState,
  id,
  isAdmin,
  onManageGroupOpen,
  onPublishSettingModalOpen,
  onUnPublish,
  onReturnToDraft,
  onResumeAccepting,
  onPauseAccepting,
  onCopyOpen,
  onDeleteOpen,
  onCustomPathOpen,
  onExportProcedure,
  onMatrixCodeOpen,
  canShowPreview,
  onOpenPreview,
  onArchiveOpen,
  onUnarchive,
}) => {
  const buttons = useButtons({
    procedure,
    onReturnToDraft,
    onPublishSettingModalOpen,
    onResumeAcceptModalOpen: onResumeAccepting,
    canShowPreview: canShowPreview,
    onOpenPreview: onOpenPreview,
  });

  const menus = useMenuButton({
    procedure,
    onCopyOpen,
    onDeleteOpen,
    onManageGroupOpen,
    onPublishSettingModalOpen,
    onUnPublish,
    onPauseAccepting,
    onCustomPathOpen,
    onExportProcedure,
    onMatrixCodeOpen,
    onArchiveOpen,
    onUnarchive,
  });

  const [archiverName, setArchiverName] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    if (procedure && procedure.isArchived() && isAdmin) {
      (async () => {
        try {
          const { data: archivedByName } = await apiClient.getUserName(
            procedure.archivedBy,
          );
          setArchiverName(archivedByName);
        } catch (e) {
          toast.error("アーカイブしたユーザー名の取得に失敗しました。");
        }
      })();
    }
  }, [isAdmin, procedure]);

  if (procedure === undefined) {
    return null;
  }

  return (
    <>
      {resourceLoadState.matchType({
        LOADING: null,
        LOAD_FAILED: null,
        NO_PERMISSION: <ContentsHeader title={`手続き詳細： ${id}`} />,
        HAS_PERMISSION: (
          <>
            {procedure.isArchived() && (
              <div className="columns has-background-warning m-0 p-2">
                <div className="column">
                  <div className="columns is-centered is-size-5 has-text-weight-bold">
                    この手続きはアーカイブされています。
                  </div>
                  <div className="columns is-centered is-size-7">
                    アーカイブした手続きを操作するには手続きを復元してください。
                  </div>
                </div>
              </div>
            )}
            <ContentsHeader
              title={procedure.name}
              caption={
                <>
                  {procedure.isGrafferManaged && (
                    <span className="has-text-weight-normal">
                      <GrafferManagedTag />
                    </span>
                  )}
                  <span className="has-text-weight-normal">
                    <ProcedureStatusTag status={procedure.publishStatus} />
                  </span>
                  <span>手続きID:{procedure.id}</span>
                  {procedure.isArchived() && (
                    <>
                      <span className="has-text-weight-normal ml-2">
                        アーカイブ日時:
                        {formatDatetime(procedure.archivedAtUnixTime * 1000)}
                      </span>
                      {archiverName && (
                        <span className="has-text-weight-normal ml-2">
                          アーカイブしたユーザー:{archiverName}
                        </span>
                      )}
                    </>
                  )}
                </>
              }
              buttons={[
                [
                  ...buttons,
                  <KebabMenu key={"procedureDetailKebabMenu"} items={menus} />,
                ],
                !procedure.isArchived() && (
                  <LinkCopyButton
                    text="公開用URLをコピー"
                    value={procedure.doorPageURL(orgSlug, currentCustomPath)}
                  />
                ),
              ]}
            />
          </>
        ),
      })}
    </>
  );
};
